import cn from "classnames";
import { memo, useCallback, useMemo, useState } from "react";
import {
  Accordion,
  Loader,
  Text,
  CURRENCY_SYMBOLS,
} from "@rshbintech.dboul/core-ui";
import { bulkSignOrchestratorApi } from "@rshbintech.dboul/http";
import { logger } from "@rshbintech.dboul/logger";
import {
  ACCORDION_DATA_ERROR,
  DOCUMENT_SIGNING_STAGES,
  DOCUMENT_TYPES_DICTIONARY,
} from "../constants";
import { ProgressIndicator } from "./progress-indicator";
import { ACCOUNT, PERCENTS_FOR_STEP } from "./constants";
import { CcbDataField, CcbDataType, DocumentCardProps } from "./types";
import { convertCCbData } from "./utils";
import { DocumentIcon } from "./document-icon";
import styles from "./styles.scss";

export const DocumentCard = memo(
  ({
    taskId,
    document,
    signingStage,
    errorText,
    isLast,
  }: DocumentCardProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const [ccbData, setCcbData] = useState<CcbDataType[]>([]);

    const handleClick = async () => {
      if (!ccbData?.length && taskId) {
        try {
          const getSpecificCcbResponse =
            await bulkSignOrchestratorApi.api.getSpecificCcb(taskId, {
              id: document.id,
              system: document.systemShortName,
              type: document.type.code,
            });

          const isCcbData = (ccbData?: string): ccbData is string =>
            !!ccbData && typeof ccbData === "string";

          if (
            getSpecificCcbResponse?.data?.ccb &&
            isCcbData(getSpecificCcbResponse.data?.ccb)
          ) {
            if (isCcbData(getSpecificCcbResponse.data?.ccbFormat)) {
              // сформировать массив ccbDataField с элементами в нужном порядке
              const ccbData = JSON.parse(getSpecificCcbResponse.data.ccb);

              const ccbDataFormat = JSON.parse(
                getSpecificCcbResponse.data.ccbFormat
              );

              const { fields } = ccbData.document;

              if (!fields) {
                throw new Error("Неверное значение поля ccbData");
              }

              const convertedCcbData = convertCCbData(
                ccbDataFormat.ccb,
                fields
              );

              setCcbData(convertedCcbData);
            } else {
              const monolithCcbData: CcbDataField[] = JSON.parse(
                getSpecificCcbResponse.data.ccb
              ).data;

              if (monolithCcbData) {
                setCcbData(monolithCcbData);
              }
            }
          }
        } catch (error) {
          logger.sendError(error);
          setIsLoading(false);
        }

        setIsLoading(false);
      }
      //на случай, если отрубится криптопро, чтобы клиент не смотрел на бесконечную загрузку ККБ

      if (!taskId) {
        setIsLoading(false);
      }
    };

    const progressInPercent = useMemo(() => {
      if (signingStage === DOCUMENT_SIGNING_STAGES.FINISHED_WITH_ERROR) {
        return 100;
      }

      if (!signingStage || signingStage === DOCUMENT_SIGNING_STAGES.EMPTY) {
        return 0;
      }

      return signingStage * PERCENTS_FOR_STEP;
    }, [signingStage]);

    const documentType =
      DOCUMENT_TYPES_DICTIONARY[
        document.type.code as keyof typeof DOCUMENT_TYPES_DICTIONARY
      ] || "";

    const preparedDate = new Date(document.date);
    const correctDate = new Intl.DateTimeFormat("ru-RU", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    }).format(preparedDate);

    const currencySymbol = document.currency
      ? CURRENCY_SYMBOLS[document.currency as keyof typeof CURRENCY_SYMBOLS]
      : "";

    const documentTitle = (
      <div className={styles["document-card-title"]}>
        <div className={styles["icon-container"]}>
          <DocumentIcon signingStage={signingStage} isError={!!errorText} />
        </div>
        <div className={styles["document-info"]}>
          <Text size="large">
            {`${documentType} № ${document.number} от ${correctDate}`}
          </Text>
          <div className={styles["document-details"]}>
            <Text size="large" className={styles.value} weight="medium">
              {`${document.amount} ${currencySymbol}`}
            </Text>
            <Text
              className={styles.account}
            >{`${ACCOUNT} ● ${document.contragent.account}`}</Text>
            <Text>{`${document.contragent.name}`}</Text>
          </div>
          {!!errorText && (
            <Text className={styles["signing-error"]} size="small">
              {errorText}
            </Text>
          )}

          <ProgressIndicator
            progressInPercent={progressInPercent}
            isError={!!errorText}
          />
        </div>
      </div>
    );

    const getAccordionSingleItem = (
      { description, value }: { description: string; value: string },
      paddingLeft = 0,
      key?: number
    ) => (
      <div
        className={styles["document-properties"]}
        style={{ paddingLeft }}
        key={key}
      >
        <Text size="medium" className={styles.description}>
          {description}
        </Text>
        <Text size="large" className={styles.value}>
          {value}
        </Text>
      </div>
    );

    const getAccordionList = (
      { description = "", value }: CcbDataType,
      key: number,
      paddingLeft = 0
    ) => {
      const isValueArray = Array.isArray(value);
      const isValueEmptyArray = isValueArray && !value.length;
      const singleValue = isValueArray ? "" : (value as string);

      return isValueArray && !isValueEmptyArray ? (
        <div key={key}>
          {getAccordionSingleItem(
            { description, value: singleValue },
            paddingLeft
          )}

          {value.map((nestedList) =>
            nestedList.map((list: CcbDataType, key: number) =>
              getAccordionList(list, key, paddingLeft + 10)
            )
          )}
        </div>
      ) : (
        getAccordionSingleItem(
          { description, value: singleValue },
          paddingLeft,
          key
        )
      );
    };

    const renderCcb = (dataToShow: CcbDataType[]) => (
      <>
        {!!dataToShow?.length &&
          dataToShow.map((data, index) => getAccordionList(data, index))}
      </>
    );

    const renderCcbData = useCallback(() => {
      if (!!ccbData?.length) {
        return <>{renderCcb(ccbData)}</>;
      }

      return (
        <>
          <Text size="medium" className={styles.description}>
            {ACCORDION_DATA_ERROR}
          </Text>
        </>
      );
    }, [ccbData]);

    return (
      <Accordion
        onClick={handleClick}
        title={documentTitle}
        className={cn(styles["document-card"], {
          [styles["last-document-card"]]: isLast,
        })}
      >
        {isLoading ? <Loader /> : renderCcbData()}
      </Accordion>
    );
  }
);
